/**
 * V4JsonView — Phase 7d redesign.
 *
 * V3-inspired light chrome around a dark, syntax-highlighted code
 * area. The previous V4 design was entirely dark (#0e1116 surface,
 * grey footer) which felt out of place in the otherwise white canvas
 * shell. The operator feedback was clear: V3's mixed light/dark layout
 * was more legible and more "native" to the platform.
 *
 * NEW SHAPE (mirrors V3 CanvasPageV3.jsx:16494-16660):
 *
 *   ┌── white header ─────────────────────────────────────────────┐
 *   │  📄 Workflow definition  [JSON pill]  3 nodes · 2 connections │
 *   │                                          [Collapse] [Copy] │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │  light grey hint banner:                                     │
 *   │  Tip — Copy this JSON and paste it into AI Builder to        │
 *   │  recreate or modify the workflow.                            │
 *   ├── dark code area (light text + syntax highlighting) ─────────┤
 *   │  1  {                                                         │
 *   │  2    "nodes": [                                              │
 *   │  3      { "id": "a", "type": "manual_start", ... }            │
 *   │  …                                                            │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │  white footer: 3 nodes · 2 connections · 47 lines · 1.2 KB  │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Color choices:
 *   - Outer chrome (header, hint, footer): white + stone neutrals
 *   - Code area: dark for the code itself (better for sustained reading)
 *     but contained — doesn't take over the whole panel.
 *   - Syntax highlighting: keys = sky, strings = emerald, numbers = amber,
 *     booleans = violet, null = stone (matches GitHub's light/dark codes)
 *
 * Tokens: V2 BRAND for the green pill, ACCENT semantic for syntax,
 * S stone for chrome.
 */

import React, { useMemo, useState } from 'react';
import { Copy, Check, Download, FileJson, ChevronDown, ChevronUp } from 'lucide-react';
import { BRAND, S, ACCENT, SPACING, RADIUS, FONT, WEIGHT } from './v4Tokens';

export default function V4JsonView({ payload, workflowName, embedded = false }) {
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Stringify once per payload change. Pretty-printed with 2-space indent.
  const { json, lines, byteSize, nodeCount, connCount } = useMemo(() => {
    const safe = {
      nodes: Array.isArray(payload?.nodes) ? payload.nodes : [],
      connections: Array.isArray(payload?.connections) ? payload.connections : [],
    };
    let text = '';
    try { text = JSON.stringify(safe, null, 2); }
    catch (_) { text = '/* JSON.stringify failed — payload contained circular references */'; }
    const splitLines = text.split('\n');
    let bytes = text.length;
    try {
      if (typeof TextEncoder !== 'undefined') {
        bytes = new TextEncoder().encode(text).length;
      }
    } catch (_) { /* keep char-count fallback */ }
    return {
      json: text,
      lines: splitLines,
      byteSize: bytes,
      nodeCount: safe.nodes.length,
      connCount: safe.connections.length,
    };
  }, [payload]);

  const handleCopy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(json);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };
  const handleDownload = () => {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workflowName || 'workflow'}-definition.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fmtBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div style={wrapperStyle}>
      {/* ── Header (white, V3-inspired) ──────────────────────────── */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.s, minWidth: 0, flex: 1 }}>
          <FileJson size={13} aria-hidden="true" style={{ color: S[700], flexShrink: 0 }} />
          <span style={{
            fontSize: FONT.md, fontWeight: WEIGHT.semibold, color: S[900],
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            Workflow definition
          </span>
          <span style={{
            fontSize: FONT.xs, fontWeight: WEIGHT.bold, color: BRAND.dark,
            background: BRAND.light,
            padding: '2px 7px', borderRadius: RADIUS.pill,
            letterSpacing: 0.4, textTransform: 'uppercase',
            flexShrink: 0,
          }}>
            JSON
          </span>
          <span style={{
            fontSize: FONT.xs, color: S[500],
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            flexShrink: 1, minWidth: 0,
          }}>
            {nodeCount} node{nodeCount === 1 ? '' : 's'} · {connCount} connection{connCount === 1 ? '' : 's'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Expand' : 'Collapse'}
            aria-label={collapsed ? 'Expand JSON' : 'Collapse JSON'}
            style={toolbarBtnStyle}
          >
            {collapsed ? <ChevronDown size={11} aria-hidden="true" /> : <ChevronUp size={11} aria-hidden="true" />}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            title="Download definition.json"
            aria-label="Download definition.json"
            style={toolbarBtnStyle}
          >
            <Download size={11} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={handleCopy}
            title="Copy JSON to clipboard"
            aria-label="Copy JSON to clipboard"
            style={{
              ...toolbarBtnStyle,
              color: copied ? ACCENT.emerald.text : S[700],
              background: copied ? ACCENT.emerald.bg : '#ffffff',
              borderColor: copied ? ACCENT.emerald.border : S[200],
            }}
          >
            {copied ? <Check size={11} aria-hidden="true" /> : <Copy size={11} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* ── Hint banner (light grey, V3-style) ──────────────────── */}
      <div style={hintStyle}>
        <strong style={{ fontWeight: WEIGHT.semibold }}>Tip.</strong>
        &nbsp;Copy this JSON and paste it into the AI Builder Import tab to recreate or modify the workflow.
      </div>

      {/* ── Code area (dark, syntax-highlighted) ────────────────── */}
      {!collapsed && (
        <div style={bodyStyle}>
          <pre style={preStyle} aria-label="Workflow definition JSON">
            {lines.map((line, i) => (
              <div key={i} style={lineRowStyle}>
                <span style={lineNumStyle}>{i + 1}</span>
                <span
                  style={lineContentStyle}
                  dangerouslySetInnerHTML={{ __html: highlightLine(line) }}
                />
              </div>
            ))}
          </pre>
        </div>
      )}

      {/* ── Footer (white) ──────────────────────────────────────── */}
      <div style={footerStyle}>
        <span><strong style={{ color: S[800], fontWeight: WEIGHT.semibold }}>{nodeCount}</strong> node{nodeCount === 1 ? '' : 's'}</span>
        <span style={dotStyle}>·</span>
        <span><strong style={{ color: S[800], fontWeight: WEIGHT.semibold }}>{connCount}</strong> connection{connCount === 1 ? '' : 's'}</span>
        <span style={dotStyle}>·</span>
        <span><strong style={{ color: S[800], fontWeight: WEIGHT.semibold }}>{lines.length}</strong> line{lines.length === 1 ? '' : 's'}</span>
        <span style={dotStyle}>·</span>
        <span>{fmtBytes(byteSize)}</span>
      </div>
    </div>
  );
}

/* ───── tiny syntax highlighter ─────────────────────────────────────
 * Lightweight regex-based highlighter. Good enough for well-formed
 * JSON (which is always what we render — we control the JSON.stringify).
 * Avoids pulling in a full tokenizer for ~50KB of payload typical of
 * a workflow definition.
 *
 * The result is sanitized — we escape HTML in the input string before
 * applying highlight wrappers, so injection via node label is impossible.
 * ─────────────────────────────────────────────────────────────────── */
function highlightLine(line) {
  const escaped = line
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    // Keys: "foo":
    .replace(/&quot;([^&]*?)&quot;:/g, '<span style="color:#7dd3fc">"$1"</span>:')
    // String values: : "foo"
    .replace(/: &quot;(.*?)&quot;/g, ': <span style="color:#86efac">"$1"</span>')
    // Numbers: : 42
    .replace(/: (-?\d+(?:\.\d+)?)/g, ': <span style="color:#fbbf24">$1</span>')
    // Booleans + null: : true | : false | : null
    .replace(/: (true|false)/g, ': <span style="color:#c4b5fd">$1</span>')
    .replace(/: (null)/g, ': <span style="color:#94a3b8">$1</span>');
}

/* ───── styles ────────────────────────────────────────────────────── */

const wrapperStyle = {
  display: 'flex', flexDirection: 'column',
  width: '100%', height: '100%',
  background: '#ffffff',
  fontFamily: 'inherit',
  overflow: 'hidden',
};

const headerStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: SPACING.s,
  padding: `${SPACING.s + 2}px ${SPACING.m}px`,
  background: '#ffffff',
  borderBottom: `1px solid ${S[200]}`,
  flexShrink: 0,
};

const toolbarBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 24, height: 24, padding: 0,
  color: S[700],
  background: '#ffffff',
  border: `1px solid ${S[200]}`,
  borderRadius: RADIUS.sm,
  cursor: 'pointer', fontFamily: 'inherit',
  transition: 'all 160ms ease',
};

const hintStyle = {
  fontSize: FONT.xs,
  color: S[600],
  background: S[50],
  padding: `${SPACING.xs + 2}px ${SPACING.m}px`,
  borderBottom: `1px solid ${S[200]}`,
  lineHeight: 1.5,
  flexShrink: 0,
};

const bodyStyle = {
  flex: 1, overflow: 'auto', minHeight: 0,
  background: '#0f172a',  // slate-900 — easier on eyes than near-black
};

const preStyle = {
  margin: 0,
  padding: `${SPACING.m}px 0`,
  fontFamily: '"JetBrains Mono", "Fira Code", SFMono-Regular, Menlo, Monaco, "Courier New", monospace',
  fontSize: 12, lineHeight: 1.55,
  color: '#e2e8f0',  // slate-200 — high contrast on slate-900 bg
  background: 'transparent',
  whiteSpace: 'pre',
};

const lineRowStyle = {
  display: 'flex', alignItems: 'flex-start', gap: SPACING.m,
};

const lineNumStyle = {
  flexShrink: 0,
  display: 'inline-block',
  width: 40, paddingLeft: SPACING.m, paddingRight: SPACING.s,
  textAlign: 'right',
  color: '#64748b',  // slate-500
  userSelect: 'none',
  fontVariantNumeric: 'tabular-nums',
  fontSize: FONT.sm,
};

const lineContentStyle = {
  flex: 1, minWidth: 0,
  whiteSpace: 'pre',
  paddingRight: SPACING.l,
};

const footerStyle = {
  display: 'flex', alignItems: 'center', gap: SPACING.xs + 2,
  padding: `${SPACING.xs + 2}px ${SPACING.m}px`,
  fontSize: FONT.xs, color: S[500],
  background: '#ffffff',
  borderTop: `1px solid ${S[200]}`,
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
};

const dotStyle = { color: S[300] };
